import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Paper,
  Typography,
  Stack,
  Box,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Container,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
  CircularProgress,
  IconButton,
  FormControl,
  MenuItem,
  Select,
  Pagination,
  InputAdornment,
  Fab,
  Badge,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import SkeletonLoading from "./skeletonLoading/SkeletonLoading";
import ErrorPage from "../../../../Components/GeneralComponents/errorPage/ErrorPage";
import useUpdate from "../../../../api/useUpdate";
import { useTranslation } from "react-i18next";
import PrintIcon from "@mui/icons-material/Print";
import Print from "./Print";

const CollectPrescriptionFees = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState("today");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPrescriptions, setTotalPrescriptions] = useState(0);

  // Helper function to build query string
  const buildQuery = (search, start, end, dateRange, status) => {
    let q = "";
    if (search) q += `search=${encodeURIComponent(search)}`;

    // Handle custom date range
    if (dateRange === "custom") {
      if (start) q += `${q ? "&" : ""}start_datetime=${start}T00:00:00`;
      if (end) q += `${q ? "&" : ""}end_datetime=${end}T23:59:59`;
    } else {
      // Map frontend date_range to backend type parameter
      const typeMapping = {
        today: "daily",
        week: "weekly",
        month: "monthly",
        year: "yearly",
        custom: "",
      };
      const backendType = typeMapping[dateRange];
      if (backendType) {
        q += `${q ? "&" : ""}type=${backendType}`;
      }
    }
    // Add status filter - only if not "all"
    if (status !== "all") {
      q += `${q ? "&" : ""}payment_status=${status}`;
    }
    return q;
  };

  // Build initial query with default filters (today, all statuses)
  const [query, setQuery] = useState(() => buildQuery("", "", "", "today", "all"));

  // Building query for backend when filters change
  useEffect(() => {
    const newQuery = buildQuery(searchTerm, startDate, endDate, dateRangeFilter, statusFilter);
    setQuery(newQuery);
  }, [searchTerm, startDate, endDate, dateRangeFilter, statusFilter]);

  const {
    data = { results: [], count: 0 },
    refetch,
    isLoading,
    isError,
  } = useFetchObjects(
    ["collect-prescription", page, rowsPerPage, query],
    `prescriptions/?${query}&page=${page}&page_size=${rowsPerPage}`,
    token,
    {
      refetchInterval: 5000,
      refetchOnWindowFocus: true,
    }
  );

  // Remove the refetch useEffect - useFetchObjects will handle refetching automatically
  // The refetch on query change was causing the data to disappear

  useEffect(() => {
    setTotalPrescriptions(data.count);
  }, [data.count]);

  const { handleUpdate, isLoading: updateLoading } = useUpdate(
    "prescriptions",
    token
  );

  const handleStatusChange = (id) => {
    const formData = new FormData();
    formData.append("payment_status", 2);
    handleUpdate(id, formData);
  };

  const handlePrintClick = (order) => {
    setPrintData(order);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleClearSearch = () => setSearchTerm("");
  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
  };

  // Calculate totals for different statuses
  const pageTotal = data.results.reduce((sum, prescription) => {
    return sum + Number(prescription.total_price || 0);
  }, 0);

  const paidTotal = data.results
    .filter((p) => p.payment_status === 2)
    .reduce(
      (sum, prescription) => sum + Number(prescription.total_price || 0),
      0
    );

  const pendingTotal = data.results
    .filter((p) => p.payment_status === 1)
    .reduce(
      (sum, prescription) => sum + Number(prescription.total_price || 0),
      0
    );

  const paidCount = data.results.filter((p) => p.payment_status === 2).length;
  const pendingCount = data.results.filter(
    (p) => p.payment_status === 1
  ).length;
  const pageCount = data.results.length;

  if (isLoading) return <SkeletonLoading />;
  if (isError)
    return (
      <ErrorPage
        errorMessage={data?.error?.message || t("reception.errorLoading")}
      />
    );

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 1:
        return t("reception.pending", "Pending");
      case 2:
        return t("reception.paid", "Paid");
      case 3:
        return t("reception.canceled", "Canceled");
      default:
        return t("reception.unknown", "Unknown");
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 1:
        return "warning";
      case 2:
        return "success";
      case 3:
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 5,
          mb: 4,
          gap: 2,
          p: 2,
          bgcolor: "#f4f6fa",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          color="primary"
          sx={{ mb: isSmallScreen ? 2 : 0 }}
        >
          {t("reception.collectFeesTitle", "Collect Prescription Fees")}
        </Typography>
        <Stack
          direction={isSmallScreen ? "column" : "row"}
          gap={2}
          sx={{ width: isSmallScreen ? "100%" : "auto" }}
        >
          <TextField
            variant="outlined"
            size="small"
            fullWidth={isSmallScreen}
            value={searchTerm}
            placeholder={t(
              "reception.searchPrescription",
              "Search prescription, doctor, patient..."
            )}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              sx: { borderRadius: 2, bgcolor: "#fff", boxShadow: 1 },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 200,
              maxWidth: 320,
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: 1,
            }}
          />
          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: 180,
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Select
              value={dateRangeFilter}
              onChange={(e) => setDateRangeFilter(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <CalendarTodayIcon color="primary" />
                </InputAdornment>
              }
            >
              <MenuItem value="today">{t("reception.today", "Today")}</MenuItem>
              <MenuItem value="week">
                {t("reception.thisWeek", "This Week")}
              </MenuItem>
              <MenuItem value="month">
                {t("reception.thisMonth", "This Month")}
              </MenuItem>
              <MenuItem value="year">
                {t("reception.thisYear", "This Year")}
              </MenuItem>
              <MenuItem value="custom">
                {t("reception.customRange", "Custom Range")}
              </MenuItem>
            </Select>
          </FormControl>
          {dateRangeFilter === "custom" && (
            <>
              <TextField
                type="date"
                variant="outlined"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputProps={{
                  endAdornment: startDate && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setStartDate("")} size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 180,
                  bgcolor: "#fff",
                  borderRadius: 2,
                  boxShadow: 1,
                }}
                label={t("reception.startDate", "Start Date")}
              />
              <TextField
                type="date"
                variant="outlined"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputProps={{
                  endAdornment: endDate && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setEndDate("")} size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 180,
                  bgcolor: "#fff",
                  borderRadius: 2,
                  boxShadow: 1,
                }}
                label={t("reception.endDate", "End Date")}
              />
              {(startDate || endDate) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearDates}
                  startIcon={<ClearIcon />}
                  sx={{
                    minWidth: 120,
                    bgcolor: "#fff",
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  {t("reception.clearDates", "Clear Dates")}
                </Button>
              )}
            </>
          )}
          <FormControl
            variant="outlined"
            size="small"
            sx={{
              minWidth: 180,
              bgcolor: "#fff",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">
                {t("reception.allStatus", "All Status")}
              </MenuItem>
              <MenuItem value="1">{t("reception.pending", "Pending")}</MenuItem>
              <MenuItem value="2">{t("reception.paid", "Paid")}</MenuItem>
              <MenuItem value="3">
                {t("reception.canceled", "Canceled")}
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      <Grid container spacing={3} padding="20px" mt={3}>
        {data.results.length === 0 ? (
          <Grid item xs={12}>
            <Box textAlign="center" mt={4}>
              <Typography color="text.secondary">
                {t("reception.noMatching", "No matching prescriptions found.")}
              </Typography>
            </Box>
          </Grid>
        ) : (
          data.results.map((prescription, index) => {
            const isPaid = prescription.payment_status === 2;
            return (
              <Grid item xs={12} md={6} key={prescription.id}>
                <Paper
                  elevation={isPaid ? 2 : 4}
                  sx={{
                    mb: 3,
                    p: 3,
                    borderRadius: 3,
                    width: "100%",
                    background: isPaid
                      ? "linear-gradient(90deg, #f5f5f5 70%, #f4f6fa 100%)"
                      : "linear-gradient(90deg, #ffebee 60%, #fffde7 100%)",
                    borderLeft: isPaid
                      ? "4px solid #009688"
                      : "4px solid #d32f2f",
                    transition: "box-shadow 0.2s",
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    mb={2}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="primary"
                      sx={{ fontSize: isSmallScreen ? "1rem" : "1.1rem" }}
                    >
                      {t("reception.prescription", "Prescription")} #
                      {prescription.id} &mdash; {prescription.date}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={getPaymentStatusLabel(
                          prescription.payment_status
                        )}
                        color={getPaymentStatusColor(
                          prescription.payment_status
                        )}
                        sx={{
                          fontWeight: "bold",
                          fontSize: isSmallScreen ? "0.8rem" : "0.95rem",
                          px: 1,
                        }}
                      />
                      <IconButton
                        color="primary"
                        onClick={() => handlePrintClick(prescription)}
                        sx={{ ml: 1 }}
                      >
                        <PrintIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{ mt: 1, mb: 1 }}
                  >
                    <Typography fontSize={isSmallScreen ? "0.92rem" : "1rem"}>
                      <strong>{t("reception.doctor", "Doctor")}:</strong>{" "}
                      {prescription.doctor_display}
                    </Typography>
                    <Typography fontSize={isSmallScreen ? "0.92rem" : "1rem"}>
                      <strong>{t("reception.patient", "Patient")}:</strong>{" "}
                      {prescription.patient_display}
                    </Typography>
                    <Typography fontSize={isSmallScreen ? "0.92rem" : "1rem"}>
                      <strong>
                        {t("pharmacy.totalPrice", "Total Price")}:
                      </strong>{" "}
                      {Number(prescription.total_price) +
                        Number(prescription?.discount)}{" "}
                      ؋
                    </Typography>
                    {prescription?.discount > 0 && (
                      <Typography fontSize={isSmallScreen ? "0.92rem" : "1rem"}>
                        <strong>{t("pharmacy.discount", "Discount")}:</strong>{" "}
                        {prescription.discount} ؋
                      </Typography>
                    )}
                    {prescription?.discount > 0 && (
                      <Typography fontSize={isSmallScreen ? "0.92rem" : "1rem"}>
                        <strong>
                          {t("pharmacy.afterDiscount", "After Discount")}:
                        </strong>{" "}
                        {prescription.total_price} ؋
                      </Typography>
                    )}
                  </Stack>

                  {!isPaid && (
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={2}
                      sx={{ mt: 1 }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={updateLoading}
                        startIcon={
                          updateLoading && (
                            <CircularProgress size={20} color="inherit" />
                          )
                        }
                        onClick={() => handleStatusChange(prescription.id)}
                        sx={{
                          minWidth: 160,
                          fontWeight: "bold",
                          borderRadius: 2,
                          py: 1,
                          background:
                            "linear-gradient(90deg, #d32f2f, #f44336)",
                          "&:hover": {
                            background:
                              "linear-gradient(90deg, #f44336, #d32f2f)",
                            boxShadow: 2,
                          },
                        }}
                      >
                        {updateLoading
                          ? t("reception.loading", "Processing...")
                          : t("reception.collectFees", "Collect Fees")}
                      </Button>
                    </Stack>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${prescription.id}-content`}
                      id={`panel${prescription.id}-header`}
                    >
                      <Typography
                        fontWeight="bold"
                        color={!isPaid ? "error.main" : "text.primary"}
                        fontSize={isSmallScreen ? "1rem" : "1.08rem"}
                      >
                        {t(
                          "reception.medicinePrescription",
                          "Prescribed Medicines"
                        )}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {prescription.drugs && prescription.drugs.length > 0 ? (
                        <Box sx={{ overflowX: "auto" }}>
                          <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell align="center">
                                  {t("reception.medicine", "Medicine")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("reception.dosage", "Dosage")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("reception.unitPerMeal", "Units/Meal")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("reception.quantity", "Quantity")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("reception.notation", "Notation")}
                                </TableCell>
                                <TableCell align="center">
                                  {t("reception.isBeforeMeal", "Before Meal?")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody sx={{ bgcolor: "#e7f6f7" }}>
                              {prescription.drugs.map((drug, idx) => (
                                <TableRow key={drug.id}>
                                  <TableCell align="center">
                                    <Typography fontWeight="bold">
                                      {drug.stock_medicine_display}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    {drug.dosage}
                                  </TableCell>
                                  <TableCell align="center">
                                    {drug.units_per_meal}
                                  </TableCell>
                                  <TableCell align="center">
                                    {drug.quantity}
                                  </TableCell>
                                  <TableCell align="center">
                                    {drug.notation}
                                  </TableCell>
                                  <TableCell align="center">
                                    {drug.is_before === "before"
                                      ? t("pharmacy.before", "Before")
                                      : drug.is_before === "after"
                                      ? t("pharmacy.after", "After")
                                      : drug.is_before === "med"
                                      ? t("pharmacy.med", "With Meal")
                                      : drug.is_before}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {/* Extra medicines list */}
                              {prescription?.extra_medicines?.map((item) => (
                                <TableRow
                                  key={item.id}
                                  sx={{
                                    backgroundColor: "#f0f4f8",
                                  }}
                                >
                                  <TableCell align="center">
                                    <Typography fontWeight="bold">
                                      {item.title}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.dosage}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.units_per_meal}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.quantity}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.notation}
                                  </TableCell>
                                  <TableCell align="center">
                                    {item.is_before}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : (
                        <Typography>
                          {t(
                            "reception.noMedicine",
                            "No medicines for this prescription."
                          )}
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              </Grid>
            );
          })
        )}

        {/* Print Component */}
        {printData && (
          <div ref={printRef} style={{ display: "none" }}>
            <Print data={printData} />
          </div>
        )}

        {/* Page Total Display */}
        {pageCount > 0 && (
          <Box
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {/* Total Amount */}
            {/* <Fab
              color="primary"
              sx={{
                minWidth: 200,
                height: 50,
                borderRadius: 25,
                display: "flex",
                flexDirection: "row",
                gap: 1,
              }}
            >
              <AttachMoneyIcon />
              <Typography variant="h6" fontWeight="bold">
                {pageTotal.toLocaleString()} ؋
              </Typography>
            </Fab> */}

            {/* Breakdown */}
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "background.paper",
                boxShadow: 3,
                minWidth: 200,
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Breakdown:
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" color="success.main">
                  Paid ({paidCount}):
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="success.main"
                >
                  {paidTotal.toLocaleString()} ؋
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="warning.main">
                  Pending ({pendingCount}):
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="warning.main"
                >
                  {pendingTotal.toLocaleString()} ؋
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Pagination Controls */}
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={4}
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <Typography variant="body2" mr={2}>
                {t("reception.rowsPerPage", "Rows per page")}:
              </Typography>
              <FormControl variant="standard" sx={{ minWidth: 120 }}>
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  label={t("reception.rowsPerPage", "Rows per page")}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalPrescriptions / rowsPerPage)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CollectPrescriptionFees;
